import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';

// Query Keys
export const chargerQueryKeys = {
  all: ['chargers'],
  lists: (params) => [...chargerQueryKeys.all, 'list', params],
  list: (params) => [...chargerQueryKeys.lists(params)],
  details: () => [...chargerQueryKeys.all, 'detail'],
  detail: (id) => [...chargerQueryKeys.details(), id],
  byStation: (cpoStationId) => [...chargerQueryKeys.all, 'byStation', cpoStationId],
};

// 충전기 목록 조회
export const useChargers = (params = {}) => {
  return useQuery({
    queryKey: chargerQueryKeys.list(params),
    queryFn: () => api.get('/drivecore/resource/api/Chargers', { params }),
    staleTime: 5 * 60 * 1000,
  });
};

// 충전기 상세 조회
export const useCharger = (id) => {
  return useQuery({
    queryKey: chargerQueryKeys.detail(id),
    queryFn: async () => {
      const response = await api.get(`/drivecore/resource/api/Chargers/${id}`);
      // 단일 충전기 조회의 경우 Items[0] 또는 직접 응답 데이터 반환
      return response || null;
    },
    enabled: !!id,
  });
};

// CpoStationId로 충전기 목록 조회
export const useChargersByStation = (cpoStationId) => {
  return useQuery({
    queryKey: chargerQueryKeys.byStation(cpoStationId),
    queryFn: async () => {
      if (!cpoStationId) return { Items: [] };
      
      try {
        const response = await api.get('/drivecore/resource/api/Chargers', {
          params: { CpoStationId: cpoStationId }
        });
        
        // response.data가 있으면 사용, 없으면 response 자체 사용
        const data = response.data || response;
        
        return data || { Items: [] };
      } catch (error) {
        console.error('충전기 조회 실패:', error);
        return { Items: [] };
      }
    },
    enabled: !!cpoStationId,
    staleTime: 5 * 60 * 1000, // 5분간 캐시 유지
    cacheTime: 15 * 60 * 1000, // 15분간 메모리에 보관
  });
};

// 충전기 생성
export const useCreateCharger = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (chargerData) => api.post('/drivecore/resource/api/Chargers', chargerData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chargerQueryKeys.lists() });
    },
  });
};

// 충전기 수정
export const useUpdateCharger = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ data }) => api.put(`/drivecore/resource/api/Chargers`, data),
    onSuccess: (data, variables) => {
      // 개별 충전기 상세 캐시 업데이트
      queryClient.setQueryData(chargerQueryKeys.detail(variables.data.ChargerSysId), data);
      
      // 모든 충전기 관련 쿼리 무효화
      queryClient.invalidateQueries({ queryKey: chargerQueryKeys.all });
      
      // 특정 충전소의 충전기 목록도 무효화
      if (variables.data.cpoStationId) {
        queryClient.invalidateQueries({ queryKey: chargerQueryKeys.byStation(variables.data.cpoStationId) });
      }
    },
  });
};

// 충전기 삭제
export const useDeleteCharger = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => api.delete(`/drivecore/resource/api/Chargers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chargerQueryKeys.lists() });
    },
  });
};
